import { useCallback, useEffect, useRef } from 'react';

import { sessionId, useApplicationState } from '@atlas/core';

import { POSTHOG_EVENTS, POSTHOG_SEARCH_OUTCOME } from 'common/constants';

import usePostHog from '../usePostHog';

const useCasinoSearchPosthog = ({ isSearchActive, searchQuery }) => {
  const postHog = usePostHog();
  const isSentRef = useRef(false);
  const outcomeRef = useRef(POSTHOG_SEARCH_OUTCOME.LEAVE_SEARCH);
  const gamesRef = useRef([]);
  const sessionOpenRef = useRef(false);
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;

  const { account } = useApplicationState();

  const getClientId = useCallback(() => account.id ?? null, [account.id]);

  const handleSearchOpen = useCallback(() => {
    isSentRef.current = false;
    outcomeRef.current = POSTHOG_SEARCH_OUTCOME.LEAVE_SEARCH;
    gamesRef.current = [];

    const client_id = getClientId();

    postHog.send(POSTHOG_EVENTS.SEARCH_OPENED, {
      client_id,
      opened_timestamp: Date.now(),
      session_id: sessionId,
    });

    postHog.concat(POSTHOG_EVENTS.SEARCH_PERFORMED, {
      client_id,
      game_id: null,
      outcome: null,
      result_position: null,
      results_count: null,
      search_request: searchQueryRef.current || null,
      session_id: sessionId,
      shown_timestamp: Date.now(),
    });
  }, [getClientId, postHog]);

  const handleSearchClose = useCallback(() => {
    if (isSentRef.current) return;
    isSentRef.current = true;

    postHog.send(POSTHOG_EVENTS.SEARCH_PERFORMED, { outcome: outcomeRef.current });
  }, [postHog]);

  useEffect(() => {
    if (isSearchActive) {
      handleSearchOpen();
      sessionOpenRef.current = true;
    } else if (sessionOpenRef.current) {
      handleSearchClose();
      sessionOpenRef.current = false;
    }
  }, [isSearchActive]);

  useEffect(() => {
    return () => {
      if (sessionOpenRef.current) {
        handleSearchClose();
      }
    };
  }, []);

  const handleSearchRequest = useCallback(
    value => {
      outcomeRef.current = POSTHOG_SEARCH_OUTCOME.LEAVE_SEARCH;
      postHog.concat(POSTHOG_EVENTS.SEARCH_PERFORMED, { search_request: value });
    },
    [postHog],
  );

  const handleResults = useCallback(
    games => {
      gamesRef.current = games;
      postHog.concat(POSTHOG_EVENTS.SEARCH_PERFORMED, { results_count: games.length });
    },
    [postHog],
  );

  const handleClear = useCallback(() => {
    outcomeRef.current = POSTHOG_SEARCH_OUTCOME.CLEARED;
  }, []);

  const handleResultClick = useCallback(
    gameId => {
      if (isSentRef.current) return;

      const index = gamesRef.current.findIndex(({ id }) => id === gameId);
      if (index === -1) return;

      isSentRef.current = true;

      postHog.send(POSTHOG_EVENTS.SEARCH_PERFORMED, {
        game_id: gameId,
        outcome: POSTHOG_SEARCH_OUTCOME.RESULT_CLICK,
        result_position: index + 1,
      });
    },
    [postHog],
  );

  return {
    handleClear,
    handleResultClick,
    handleResults,
    handleSearchRequest,
  };
};

export default useCasinoSearchPosthog;
